/**
 * @file AssessmentSectionList — numbered overview of the assessment's sub-sections
 * @version 0.1.2
 */

import React from 'react';
import styles from './AssessmentSectionList.module.css';

/*
 * AssessmentSectionList — numbered overview of the sub-sections inside an
 * assessment section.
 *
 *   <AssessmentSectionList
 *     items={[
 *       { label: 'Business Partnership Execution & Operations' },
 *       { label: 'Operational Achievements' },
 *     ]}
 *   />
 *
 * Renders a row of number + bright-blue-label items, each preceded by a 1px
 * hairline rule. Sits in the body slot of a section-overview divider screen,
 * below the title + intro paragraph, telling the reader how the upcoming
 * section is structured before they begin answering.
 *
 * Two layout tiers (mirrors AssessmentExample's breakpoint):
 *   md (≥768): items sit in an equal-width row, the hairline rule running down
 *     the left edge of each.
 *   sm (<768): items stack into a full-width column, each keeping its left rule.
 */

export interface AssessmentSectionListItem {
  /** Sub-section name. */
  label: string;
  /** Display number. Defaults to the item's 1-based position when omitted. */
  number?: number | string;
}

export type AssessmentSectionListLayout = 'sm' | 'md';

export interface AssessmentSectionListProps {
  /** The sub-sections, in order. */
  items: readonly AssessmentSectionListItem[];
  /**
   * Pin the layout tier (skips matchMedia auto-detect).
   *   - `sm` = stacked full-width column (viewport < 768)
   *   - `md` = equal-width row (viewport ≥ 768)
   */
  forceLayout?: AssessmentSectionListLayout;
  /**
   * Explicit width override. Without this the list fills its column (`100%`).
   * Regression sidecars pin a specific source width so screenshot baselines
   * stay deterministic.
   */
  width?: number | string;
  /**
   * Surface the list sits on. `dark` (default) uses the bright-blue label +
   * white number; `light` recolours to navy number + teal-active label + a
   * grey rule. Colour-only — layout is identical.
   */
  theme?: 'light' | 'dark';
  /** Forwarded to the root element. */
  className?: string;
}

export const AssessmentSectionList: React.FC<AssessmentSectionListProps> = ({
  items,
  forceLayout,
  width = '100%',
  theme = 'dark',
  className,
}) => {
  const [isBelow, setIsBelow] = React.useState<boolean>(forceLayout === 'sm');

  React.useEffect(() => {
    if (forceLayout) {
      setIsBelow(forceLayout === 'sm');
      return;
    }
    if (typeof window === 'undefined') return;
    // Matches the assessment screens' 768 breakpoint: the row collapses to a
    // stacked column below 768.
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsBelow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [forceLayout]);

  const rootClass = [styles.list, theme === 'light' ? styles.light : '', className].filter(Boolean).join(' ');

  return (
    <ol
      className={rootClass}
      data-layout={isBelow ? 'sm' : 'md'}
      style={{ width }}
    >
      {items.map((item, i) => (
        <li key={item.label} className={styles.item}>
          <span className={styles.number}>{item.number ?? i + 1}</span>
          <span className={styles.label}>{item.label}</span>
        </li>
      ))}
    </ol>
  );
};

AssessmentSectionList.displayName = 'AssessmentSectionList';
